import React from 'react';
import { isPast, isToday, isBefore, isSameDay } from 'date-fns';

import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Coin, { CoinColor } from 'components/Coin';
import Divider from 'components/Divider';
import EventTracking from 'lib/EventTracking';
import Icon from 'components/Icon';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text, { TextColor } from 'components/Text';
import { abbreviatedMonthAndDayFormat } from 'lib/dateTimeFormat';
import TimezoneHelper from 'lib/timezones';
import {
  CoachingAvailabilitySchedule,
  coachAvailabilityViewEventPrefix,
  CoachPause,
} from 'coaching/containers/CoachAvailabilityContainer';
import { MouseEventHandler } from 'types/typescript/eventHandlers';
import CurrentUser from 'auth/CurrentUser';

import '../styles/AvailabilitySchedulesList.scss';

export type ExactlyOneOrTwoSchedules = [
  CoachingAvailabilitySchedule,
  CoachingAvailabilitySchedule | null
];

export type Props = {
  onClickSchedule: (schedule: CoachingAvailabilitySchedule) => void;
  onClickSetUpcomingSchedule: () => void;
  onClickPauseTutoring: () => void;
  schedules: ExactlyOneOrTwoSchedules | undefined;
  timezone: string;
  pause?: CoachPause;
};

function trackEvent(event: string) {
  EventTracking.trackKey(`${coachAvailabilityViewEventPrefix}.schedules_list.${event}`);
}

export default function AvailabilitySchedulesList(props: Props) {
  const hasCoachPause = CurrentUser.getUser().hasFeature('tutoring_coach_pause.web.oct-21');

  return (
    <Box inline noShadow textAlign="left" className="AvailabilitySchedulesList">
      <BoxBody size="xsmall">
        <Text weight="semibold">
          <span tabIndex={0}>
            Availability schedules ({TimezoneHelper.convertToReadableShortCode(props.timezone)})
          </span>
        </Text>
      </BoxBody>
      <ScheduleRows {...props} />
      {hasCoachPause && <CoachPauseRow {...props} />}
    </Box>
  );
}

function CoachPauseRow(props: Props) {
  const { onClickPauseTutoring, pause } = props;
  return (
    <>
      <Divider size="flush" />
      <Row size="xxxsmall">
        {pause ? (
          <Button
            level="icon"
            onClick={onClickPauseTutoring}
            className="AvailabilitySchedulesList-coachPause">
            <MediaObject figure="right">
              <Text color="chalkboard" align="left">
                Pause starts on {`${abbreviatedMonthAndDayFormat(new Date(pause.effectiveDate))}`}
              </Text>
              <Text align="right">
                <Icon
                  name="pencil-stroke"
                  color="mascot"
                  className="AvailabilitySchedulesList-coachPause-editIcon"
                />
              </Text>
            </MediaObject>
          </Button>
        ) : (
          <Button
            level="icon"
            onClick={onClickPauseTutoring}
            weight="semibold"
            color="mascot"
            className="AvailabilitySchedulesList-pauseTutoringLink">
            Pause tutoring
          </Button>
        )}
      </Row>
    </>
  );
}

// note: this function makes the assumption that the schedules we get
// from the backend are already in chronological order.
function ScheduleRows(props: Props) {
  const { schedules, onClickSchedule, onClickSetUpcomingSchedule, pause } = props;
  if (!schedules) {
    return <span />;
  }

  const secondSchedule = schedules[1];
  const secondScheduleStartsBeforePause =
    secondSchedule && pause
      ? isBefore(new Date(secondSchedule.startDate), new Date(pause.effectiveDate)) &&
        !isSameDay(new Date(secondSchedule.startDate), new Date(pause.effectiveDate))
      : false;

  // when a coach has a scheduled pause, we only want to show the start and end dates
  // of their availability schedules if they have 2 schedules and their pause starts
  // after the second schedule starts. if they only have 1 schedule, it will already
  // be in effect and the pause will clearly be a disruption to that schedule. and if
  // they have 2 schedules but the pause starts *before* the second schedule, displaying
  // the start + end dates of the schedules along with the pause's effective date would
  // be confusing since the dates would not read in chronilogical order (since, from top
  // to bottom in the UI, we render the availability schedules first, then the pause info)
  const shouldShowScheduleDates = pause ? secondScheduleStartsBeforePause : true;

  const firstScheduleRow = (
    <ScheduleRowFilled
      schedule={schedules[0]}
      showEndDate={shouldShowScheduleDates}
      onClickSchedule={onClickSchedule}
    />
  );
  const secondScheduleRow = secondSchedule ? (
    <ScheduleRowFilled
      schedule={secondSchedule}
      showStartDate={shouldShowScheduleDates}
      onClickSchedule={onClickSchedule}
    />
  ) : (
    <ScheduleRowEmpty onClickSetUpcomingSchedule={onClickSetUpcomingSchedule} />
  );
  return (
    <>
      <Row size="xxsmall">{firstScheduleRow}</Row>
      <Row size="xsmall">{secondScheduleRow}</Row>
    </>
  );
}

function ScheduleRowEmpty({
  onClickSetUpcomingSchedule,
}: {
  onClickSetUpcomingSchedule: () => void;
}) {
  return (
    <ScheduleRow
      data-test="schedule-row-empty"
      coinColor="whiteout"
      coinBorderColor="graphite"
      labelColor="graphite"
      label="Set upcoming schedule"
      onClick={() => {
        onClickSetUpcomingSchedule();
        trackEvent('set_upcoming_schedule.click');
      }}
    />
  );
}

function ScheduleRowFilled({
  schedule,
  onClickSchedule,
  showStartDate,
  showEndDate,
}: {
  schedule: CoachingAvailabilitySchedule;
  onClickSchedule: (schedule: CoachingAvailabilitySchedule) => void;
  showStartDate?: boolean;
  showEndDate?: boolean;
}) {
  function getSubtext() {
    if (showEndDate && schedule.endDate) {
      return `Ends on ${abbreviatedMonthAndDayFormat(schedule.endDate)}`;
    } else if (showStartDate) {
      return `Starts on ${abbreviatedMonthAndDayFormat(schedule.startDate)}`;
    } else {
      return undefined;
    }
  }

  const scheduleIsActive = isPast(schedule.startDate) || isToday(schedule.startDate);
  return (
    <ScheduleRow
      data-test={`schedule-row-${schedule.uuid}`}
      coinColor={scheduleIsActive ? 'atlas' : 'graphite'}
      labelColor="chalkboard"
      label={schedule.label}
      subtext={getSubtext()}
      onClick={() => {
        onClickSchedule(schedule);
        trackEvent('edit_schedule.click');
      }}
    />
  );
}

function ScheduleRow({
  coinColor,
  coinBorderColor,
  labelColor,
  label,
  subtext,
  onClick,
}: {
  coinColor: CoinColor;
  coinBorderColor?: CoinColor;
  labelColor: TextColor;
  label: string;
  onClick: MouseEventHandler;
  subtext?: string;
}) {
  const labelComponent = subtext ? (
    <>
      <Text color={labelColor} truncate element="div" align="left">
        {label}
      </Text>
      <Text
        color="graphite"
        truncate
        lineHeight="flush"
        data-test={`subtext-${subtext}`}
        align="left">
        {subtext}
      </Text>
    </>
  ) : (
    <Text color={labelColor} truncate lineHeight="flush" align="left">
      {label}
    </Text>
  );
  return (
    <Button className="AvailabilitySchedulesList-schedule" onClick={onClick} level="icon">
      <MediaObject figure="right" center>
        <MediaObject gutter="xsmall" center={!subtext}>
          <Coin color={coinColor} border={coinBorderColor} />
          {labelComponent}
        </MediaObject>
        <Icon name="pencil-stroke" className="AvailabilitySchedulesList-schedule-editIcon" />
      </MediaObject>
    </Button>
  );
}
